import React, { memo } from 'react';
import Lozenge from '@atlaskit/lozenge';
import { MenuSection, MenuSectionHeading, MenuList } from '@atlaskit/navigation-system';
import { useHasUserEngagedWithBlueprints } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-global-products/src/controllers/blueprints-engagement/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useIsConnectionsEnabled } from '@atlassian/jira-polaris-lib-entitlement-utils/src/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';
import messages from './messages.tsx';

export const useHasUserEngagedWithBlueprintsFG = functionWithCondition(
	() => fg('jpd-prebuilt-views-lozenge-location-update'),
	() => useHasUserEngagedWithBlueprints(),
	() => true,
);

const ProductDiscoverySection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();
	const isConnectionsEntitlementEnabled = useIsConnectionsEnabled();
	const hasUserEngagedWithBlueprints = useHasUserEngagedWithBlueprintsFG();

	return (
		<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
			<MenuSectionHeading>{formatMessage(messages.heading)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'access' }}
					isSelected={currentRoute.name === 'polaris-settings-access'}
					href="/jira/settings/products/jira-product-discovery/access"
				>
					{formatMessage(messages.accessItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'globalFields' }}
					isSelected={currentRoute.name === 'polaris-settings-global-fields'}
					href="/jira/settings/products/jira-product-discovery/global-fields"
				>
					{formatMessage(messages.globalFieldsItem)}
				</LinkMenuItem>
				{fg('jpd-view-templates-sample-project') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'viewBlueprints' }}
						isSelected={currentRoute.name === 'polaris-settings-view-blueprints'}
						href="/jira/settings/products/jira-product-discovery/view-blueprints"
						elemAfter={
							hasUserEngagedWithBlueprints ? null : (
								<Lozenge appearance="new">
									{formatMessage(messages.viewBlueprintsItemLozenge)}
								</Lozenge>
							)
						}
					>
						{formatMessage(messages.viewBlueprintsItem)}
					</LinkMenuItem>
				)}
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'jpdDates' }}
					isSelected={currentRoute.name === 'polaris-settings-financial-year'}
					href="/jira/settings/products/jira-product-discovery/dates"
				>
					{formatMessage(messages.datesItem)}
				</LinkMenuItem>
				{(isConnectionsEntitlementEnabled || fg('jpd_itr-beta-removal-release')) && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'betaFeatures' }}
						isSelected={currentRoute.name === 'polaris-settings-early-access-features'}
						href="/jira/settings/products/jira-product-discovery/early-access-features"
						elemAfter={
							fg('jpd_itr-beta-removal-release') ? null : (
								<Lozenge appearance="new" isBold>
									{formatMessage(messages.betaFeaturesItemLozenge)}
								</Lozenge>
							)
						}
					>
						{formatMessage(messages.betaFeaturesItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

export default memo(ProductDiscoverySection);
